import { useEffect } from "react"
import { useComposerRuntime } from "@assistant-ui/react"

/** Persist unsent composer text in localStorage so it survives panel
 *  close, tab close, accidental refresh, or "I forgot to hit Enter."
 *
 *  Why a separate layer:
 *  - The chat backend persists messages on each successful turn, but
 *    only AFTER the user clicks send. Anything typed-but-not-sent
 *    (including text that arrived by voice transcription — the
 *    transcript is appended to the composer, not the conversation)
 *    lives only in component state until then.
 *  - assistant-ui's local runtime is recreated on each panel close, so
 *    its in-memory text doesn't survive either.
 *
 *  Behaviour:
 *  - On first mount, if a saved draft exists for ``key``, seed the
 *    composer text with it.
 *  - Subscribe to composer changes; mirror current text to
 *    localStorage on every change. Empty text removes the key (so we
 *    don't pin an empty entry in storage).
 *  - On every successful send (composer transitions from non-empty
 *    text to empty), the same subscriber clears the storage key.
 *
 *  ``key`` MUST embed the authenticated user's id so unsent text
 *  doesn't leak across accounts on a shared browser (logout, hijack
 *  release, or kiosk reuse). Pass ``null`` to opt out of persistence
 *  (e.g. while the auth context is still loading the user) — the
 *  composer just behaves without storage until the caller has a real
 *  user-scoped key. */
export function useComposerDraftPersistence(key: string | null): void {
  const composerRuntime = useComposerRuntime()

  useEffect(() => {
    if (!key) return
    let storage: Storage | null = null
    try {
      storage = window.localStorage
    } catch {
      // localStorage can be disabled (private mode, quota, etc.) —
      // silently no-op rather than crashing the composer.
      return
    }

    const saved = storage.getItem(key)
    if (saved) {
      // Only seed when the composer is empty — never clobber a value
      // that was already pre-populated (e.g. an "Edit message" handoff
      // setting initial text).
      const current = composerRuntime.getState().text ?? ""
      if (!current) {
        composerRuntime.setText(saved)
      }
    }

    const store = storage
    const flush = () => {
      const text = composerRuntime.getState().text ?? ""
      if (!text) {
        store.removeItem(key)
      } else {
        try {
          store.setItem(key, text)
        } catch {
          // Quota exceeded — drop the key so we don't grow forever.
          store.removeItem(key)
        }
      }
    }
    const unsubscribe = composerRuntime.subscribe(flush)
    return () => {
      unsubscribe()
    }
  }, [key, composerRuntime])
}
